import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, FileDown, ShieldCheck, Siren } from 'lucide-react';
import { toast } from 'sonner';
import { api, errMsg, downloadFile } from '../api';
import { Card, PageHeader, Spinner, ghostBtn, goldBtn } from '../ui';
import { ChecklistItem, Progress } from '../travel/Checklist';
import { ScoreBadge } from '../travel/TravelDashboard';
import DispatchPanel from '../travel/DispatchPanel';
import TripContact from '../travel/TripContact';
import TripEvents from '../travel/TripEvents';
import TravelerAppInvite from '../travel/TravelerAppInvite';

export default function TravelTripPage() {
  const { id } = useParams();
  const askInvite = !!useLocation().state?.askInvite;
  const [t, setT] = useState(null);
  const [cl, setCl] = useState(null);
  const load = useCallback(() => api.get(`/travel/trips/${id}`).then((r) => setT(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); api.get('/travel/compliance').then((r) => setCl(r.data.checklist)).catch(() => {}); }, [load]);
  const save = async (k, body) => { if (body) await api.put(`/travel/trips/${id}/items/${k}`, body).catch((e) => toast.error(errMsg(e))); load(); };
  if (!t || !cl) return <Spinner label="Caricamento trasferta…" />;
  const r = t.risk || {};
  return (
    <div data-testid="travel-trip-page">
      <PageHeader kicker={`Trasferta · ${t.kind}`} title={`${t.traveler} · ${t.stages?.length > 1 ? t.stages.map((s) => s.city || s.country).join(' → ') : `${t.city ? `${t.city}, ` : ''}${t.country}`}`}>
        <Link to="/portal/travel-security/compliance" className={`${ghostBtn} h-10 px-4 flex items-center gap-2 text-sm`} data-testid="trip-back"><ArrowLeft size={15} />Compliance</Link>
        <Link to="/portal/richieste/travel_protection" state={{ trip: t }} className={`${ghostBtn} h-10 px-4 flex items-center gap-2 text-sm`} data-testid="trip-protection-request"><ShieldCheck size={15} />Richiedi servizi di sicurezza</Link>
        <button onClick={() => downloadFile(`/travel/trips/${id}/briefing.pdf`, `Briefing_${t.iso3}_${t.start}.pdf`).catch((e) => toast.error(errMsg(e)))} className={`${goldBtn} h-10 px-4 text-sm inline-flex items-center gap-2`} data-testid="trip-briefing"><FileDown size={15} />Briefing PDF da firmare</button>
      </PageHeader>
      <TravelerAppInvite t={t} reload={load} ask={askInvite} />
      {t.sos?.length > 0 && (
        <Card className="p-6 mb-4 border-[#C2423A]" data-testid="trip-sos">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#E37A72] mb-3 inline-flex items-center gap-2"><Siren size={14} />Richieste di emergenza dal viaggiatore</p>
          {t.sos.map((s) => (
            <div key={s.id} className="py-2 border-t border-[#1F242C] first:border-0 text-sm" data-testid={`trip-sos-${s.id}`}>
              <b>{new Date(s.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</b> · {[s.city, s.country].filter(Boolean).join(', ')}
              {s.lat != null ? <> · <a href={`https://www.google.com/maps?q=${s.lat},${s.lon}`} target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">posizione sulla mappa</a></> : ' · posizione non disponibile'}
              {s.message && <p className="text-[#A3A3A3] mt-1">«{s.message}»</p>}
            </div>
          ))}
        </Card>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          {['pre', 'during', 'post'].map((ph) => (
            <Card key={ph} className="p-6" data-testid={`trip-phase-${ph}`}>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-2">{cl[ph].title}</p>
              {cl[ph].items.map((txt, i) => <ChecklistItem key={`${ph}_${i}`} k={`${ph}_${i}`} text={txt} state={(t.items || {})[`${ph}_${i}`]} save={save} tripId={id} />)}
            </Card>
          ))}
          <DispatchPanel trip={t} onSent={load} />
        </div>
        <div className="space-y-4 lg:sticky lg:top-24 min-w-0">
        <TripEvents trip={t} onSaved={load} />
        <TripContact trip={t} onSaved={load} />
        <Card className="p-6" data-testid="trip-risk">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-3">Rischio Paese · {t.start} → {t.end}</p>
          <Progress p={t.progress} testid="trip-progress" />
          <p className="text-sm text-[#F2F2F2] mt-4">Farnesina: <b data-testid="trip-farnesina">{r.farnesina?.label || 'n.d.'}</b></p>
          <p className="text-sm text-[#F2F2F2] mt-2 flex items-center gap-2">Indice BOND <ScoreBadge score={r.index?.score} /></p>
          {r.news?.length > 0 && <p className="text-xs text-[#E37A72] mt-3">{r.news.length} avvisi dell'ultima ora sul Paese</p>}
          <p className="text-xs text-[#8A8F97] mt-4">Usa questi dati per la voce "Valutazione del rischio Paese" e scrivi nelle note le misure decise: finiranno nel briefing PDF.</p>
          <Link to={`/portal/travel-security/${t.iso3}`} className="text-xs text-[#D4AF37] hover:underline mt-3 inline-block" data-testid="trip-country-link">Apri la scheda completa del Paese</Link>
        </Card>
        </div>
      </div>
    </div>
  );
}
